import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import MortgageSimulator, { MortgageSimulatorGroup } from "../simulator";
import {
  SimulationFormInputs,
  SimulationFormModel,
  SimulationFormParams,
} from "../types/forms";
import { peekHydrationState, clearTransientState } from "../utils/localStorage";

export const useSimulationFormModel = (
  initial: SimulationFormParams[] = [],
): SimulationFormModel => {
  const { effectiveSettings } = useGlobalSettings();
  const hasInitialized = useRef(false);

  // Read hydration state once on mount without clearing it (side-effect-free during render)
  const hydrationRef = useRef(peekHydrationState());
  const hydratedSimulations = hydrationRef.current?.simulations || initial;

  // Clear transient hydration state after mount so it doesn't persist across refreshes
  useEffect(() => {
    clearTransientState();
  }, []);

  // On first render, use hydration data (written when resuming a session)
  const [simulations, setSimulations] = useState<SimulationFormParams[]>(
    () => hydratedSimulations,
  );

  const [results] = useState<MortgageSimulatorGroup>(() => {
    const resultsGroup = new MortgageSimulatorGroup(
      effectiveSettings.equalizeLength,
    );

    // Synchronously restore simulations during initialization
    if (!hasInitialized.current) {
      if (hydratedSimulations.length > 0) {
        hydratedSimulations.forEach((sim) => {
          const simulation = new MortgageSimulator(sim.inputs);
          resultsGroup.addSimulation(simulation);
        });
      }

      hasInitialized.current = true;
    }

    return resultsGroup;
  });

  // Force re-renders when results internal state changes
  const [, setRefreshCounter] = useState(0);
  const forceRefresh = useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // Update simulations with their result indices after initialization
  useEffect(() => {
    if (
      simulations.length > 0 &&
      simulations.some((s) => s.resultIndex === undefined)
    ) {
      setSimulations((prev) =>
        prev.map((s, i) =>
          s.resultIndex === undefined ? { ...s, resultIndex: i } : s,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Sync settings changes to the results object
  useEffect(() => {
    results.setEqualLength(effectiveSettings.equalizeLength);
    forceRefresh(); // Trigger re-render after mutation
  }, [effectiveSettings.equalizeLength, results, forceRefresh]);

  const addSimulation = useCallback((sim: SimulationFormParams) => {
    setSimulations((prev) => [...prev, { ...sim, updated: true }]);
  }, []);

  const updateSimulation = useCallback(
    (
      idx: number,
      sim: Partial<SimulationFormParams> = {},
      inputs?: Partial<SimulationFormInputs>,
    ) => {
      setSimulations((prev) =>
        prev.map((s, i) =>
          i === idx
            ? {
                ...s,
                ...sim,
                inputs: {
                  ...s.inputs,
                  ...(inputs ?? {}),
                },
                updated: true,
              }
            : s,
        ),
      );
    },
    [],
  );

  const duplicateSimulation = useCallback(
    (idx: number) => {
      const simToDuplicate = simulations[idx];
      if (!simToDuplicate) return;

      // Create the duplicated simulation
      const duplicatedSim: SimulationFormParams = {
        ...simToDuplicate,
        updated: false, // Will be run immediately, so mark as clean
        resultIndex: undefined, // New simulation, so no result yet
      };

      // Add to simulations array
      setSimulations((prev) => [...prev, duplicatedSim]);

      // Immediately run the simulation and add to results
      const simulation = new MortgageSimulator(duplicatedSim.inputs);
      results.addSimulation(simulation);

      // Update the newly added simulation with its result index
      setSimulations((prev) => {
        const newIndex = prev.length - 1;
        return prev.map((s, i) =>
          i === newIndex ? { ...s, resultIndex: results.length - 1 } : s,
        );
      });

      forceRefresh(); // Trigger re-render after mutation
    },
    [results, simulations, forceRefresh],
  );

  const moveSimulation = useCallback(
    (fromIdx: number, toIdx: number) => {
      if (fromIdx === toIdx) return;
      if (fromIdx < 0 || fromIdx >= simulations.length) return;
      if (toIdx < 0 || toIdx >= simulations.length) return;

      // Move in the simulations array
      setSimulations((prev) => {
        const newSimulations = [...prev];
        const [removed] = newSimulations.splice(fromIdx, 1);
        newSimulations.splice(toIdx, 0, removed);
        return newSimulations;
      });

      // Move in the results if the simulation has been run
      const simToMove = simulations[fromIdx];
      if (simToMove?.resultIndex !== undefined) {
        // Calculate the target result index based on the destination position
        const targetResultIdx = simulations[toIdx]?.resultIndex;
        if (targetResultIdx !== undefined) {
          results.moveSimulation(simToMove.resultIndex, targetResultIdx);

          // Update all resultIndex values to reflect the new order
          setSimulations((prev) =>
            prev.map((s, i) => ({
              ...s,
              resultIndex: i,
            })),
          );

          forceRefresh();
        }
      }
    },
    [simulations, results, forceRefresh],
  );

  const removeSimulation = useCallback(
    (idx: number) => {
      const simulationToRemove = simulations[idx];

      // Remove from results if it has been run
      if (simulationToRemove?.resultIndex !== undefined) {
        results.deleteSimulation(simulationToRemove.resultIndex);

        // Update resultIndex for all remaining simulations that come after this one
        setSimulations((prev) =>
          prev
            .filter((_, i) => i !== idx)
            .map((s) => {
              if (
                s.resultIndex !== undefined &&
                simulationToRemove.resultIndex !== undefined &&
                s.resultIndex > simulationToRemove.resultIndex
              ) {
                return { ...s, resultIndex: s.resultIndex - 1 };
              }
              return s;
            }),
        );

        forceRefresh(); // Trigger re-render after mutation
      } else {
        // Not yet run, just remove from array
        setSimulations((prev) => prev.filter((_, i) => i !== idx));
      }
    },
    [results, simulations, forceRefresh],
  );

  const markAllClean = useCallback(() => {
    setSimulations((prev) => prev.map((s) => ({ ...s, updated: false })));
  }, []);

  const revertChanges = useCallback(() => {
    // Revert simulation inputs back to their last-run state from results
    setSimulations((prev) =>
      prev.map((s) => {
        if (s.updated && s.resultIndex !== undefined) {
          // Get the original inputs from the results
          const resultSimulation = results.getSimulation(s.resultIndex);
          return {
            ...s,
            inputs: { ...resultSimulation.initialParams },
            updated: false,
          };
        }
        return { ...s, updated: false };
      }),
    );
  }, [results]);

  const runSimulation = useCallback(
    async (sim: SimulationFormParams, simIndex: number) => {
      const simulation = new MortgageSimulator(sim.inputs);

      if (sim.resultIndex !== undefined) {
        // Update existing simulation result
        results.replaceSimulation(sim.resultIndex, simulation);
      } else {
        // Add new simulation result
        const resultIndex = results.length;
        results.addSimulation(simulation);
        // Update the simulation params with the result index
        setSimulations((prev) =>
          prev.map((s, i) => (i === simIndex ? { ...s, resultIndex } : s)),
        );
      }

      forceRefresh(); // Trigger re-render after mutation
    },
    [results, forceRefresh],
  );

  const runUpdatedSimulations = useCallback(async () => {
    const toRunWithIndices = simulations
      .map((s, index) => ({ sim: s, index }))
      .filter(({ sim }) => sim.updated);

    // Separate new simulations (no resultIndex) from updates (have resultIndex)
    const newSims = toRunWithIndices.filter(
      ({ sim }) => sim.resultIndex === undefined,
    );
    const updateSims = toRunWithIndices.filter(
      ({ sim }) => sim.resultIndex !== undefined,
    );

    // Run new simulations sequentially to avoid race conditions with resultIndex assignment
    for (const { sim, index } of newSims) {
      await runSimulation(sim, index);
    }

    // Run updates in parallel (safe since they already have resultIndex)
    if (updateSims.length > 0) {
      const updatePromises = updateSims.map(({ sim, index }) =>
        runSimulation(sim, index),
      );
      await Promise.all(updatePromises);
    }

    markAllClean();
  }, [simulations, runSimulation, markAllClean]);

  return {
    simulations,
    results,
    addSimulation,
    updateSimulation,
    duplicateSimulation,
    removeSimulation,
    moveSimulation,
    markAllClean,
    revertChanges,
    runSimulation,
    runUpdatedSimulations,
  };
};
